// ABOUTME: Enter Boutique gate background film — autoplay/loop lifecycle, opt-outs, and hard teardown.
// ABOUTME: Split out of EnterBoutiqueGate so that gate stays presentational (see CLAUDE.md component gates).

import { useEffect, useRef, useState } from 'react';
import { useDeviceProfile } from '../Projectionist/useViewportSize';

// The looping home-screen film (Azure Blob, ~30 MB). Streamed from the blob rather than bundled for
// the same reason as the intro film in useChapterManifest.js: it dwarfs the entire JS bundle, and
// the browser's native HTTP cache handles a file this size better than anything we would hand-roll.
// It is the same host as the reel spec and the intro film, whose connection `preloadReelAssets()`
// has already DNS/TLS-warmed during the boot-loader beat, so the first byte here is not paying for
// a cold cross-origin handshake.
//
// NEVER set crossOrigin on the element that plays this. Only the reel spec *JSON* is CORS-gated on
// that container (docs/reel-asset-cors.md); `<video>` media is exempt precisely because nothing in
// this app requests it in CORS mode. Adding the attribute would black-screen every origin that is
// not on the allowlist.
export const DEFAULT_BACKGROUND_VIDEO_SRC =
  process.env.REACT_APP_GATE_VIDEO_URL ||
  'https://mmdevukspublicstorage.blob.core.windows.net/ophion-images/HOME_PAGE_V001.mp4';

/**
 * Whether the guest has asked us, one way or another, not to pull tens of megabytes of decoration.
 * Read once per mount rather than subscribed to: `connection` fires `change` on every cell-tower
 * handover, and swapping a playing film for a still mid-read is worse than either state.
 */
export function prefersReducedData() {
  if (typeof navigator === 'undefined') return false;
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  if (!connection) return false;
  if (connection.saveData === true) return true;
  // 3g is deliberately NOT in this list: the film streams progressively and the still holds
  // underneath until it can actually play, so a merely slow link degrades rather than breaks.
  return /^(slow-2g|2g)$/.test(String(connection.effectiveType || ''));
}

/**
 * Stop the film and cancel whatever is still in flight.
 *
 * `pause()` alone is not enough — a paused element with a src keeps buffering the rest of the file,
 * so a detached one goes on eating bandwidth until GC. Dropping the source and re-loading is what
 * actually aborts the outstanding range requests. On this screen that is the whole point: the
 * moment "Enter Boutique" is clicked, every remaining byte of the gate film is competing with the
 * intro film for the first seconds inside the boutique, which is exactly the window that must not
 * stutter.
 *
 * @param {HTMLVideoElement|null} el
 */
export function releaseBackgroundVideo(el) {
  if (!el) return;
  try { el.pause(); } catch { /* detached or already torn down */ }
  try {
    el.removeAttribute('src');
    // Empty-src `load()` raises a media error in some engines; callers detach their listeners
    // before releasing so nothing reads that as a genuine playback failure.
    el.load();
  } catch { /* jsdom, and any engine that dislikes an empty src */ }
}

/**
 * Drives a decorative, muted, looping background film behind the gate.
 *
 * The still photograph stays mounted underneath and is what paints first; the film is revealed only
 * once it is genuinely playing, so a slow, blocked, or unsupported video is a no-op rather than a
 * black rectangle. Autoplay rejection (iOS Low Power Mode refuses even muted playback) is an
 * expected outcome, not an error.
 *
 * @param {string|null} src
 * @returns {{videoRef: React.RefObject<HTMLVideoElement>, videoEnabled: boolean, videoVisible: boolean}}
 */
export function useGateBackgroundVideo(src) {
  const { reducedMotion } = useDeviceProfile();
  // Lazy initialiser so the capability read happens once, at mount, not on every render.
  const [reducedData] = useState(prefersReducedData);
  const videoRef = useRef(null);
  const [videoVisible, setVideoVisible] = useState(false);

  const videoEnabled = Boolean(src) && !reducedMotion && !reducedData;

  useEffect(() => {
    if (!videoEnabled) {
      setVideoVisible(false);
      return undefined;
    }
    const el = videoRef.current;
    if (!el) return undefined;

    const reveal = () => setVideoVisible(true);
    const conceal = () => setVideoVisible(false);
    el.addEventListener('playing', reveal);
    // `playing` fires once, and it can land before this effect attaches its listener — an autoplay
    // served from a warm HTTP cache is quick. `timeupdate` fires several times a second for as long
    // as the film runs, so a missed `playing` self-corrects almost immediately instead of leaving a
    // film that is demonstrably playing stuck at opacity 0 forever.
    el.addEventListener('timeupdate', reveal);
    el.addEventListener('error', conceal);

    // React sets `muted` as a property and has historically missed it on the first commit; an
    // unmuted autoplay is refused outright by every browser we ship on, so pin it imperatively.
    el.muted = true;
    // The source is set HERE rather than as a `src` prop in the JSX, and this hook is its only
    // owner. The teardown below strips the attribute to cancel the download, and React would never
    // put it back — from its point of view the prop never changed, so its DOM diff has nothing to
    // do. That is not a hypothetical: index.js wraps the app in <React.StrictMode>, which in
    // development mounts, tears down and remounts every effect, so a JSX-owned `src` is removed on
    // that first teardown and the film is dead for the rest of the session. Assigning it here makes
    // mount and teardown symmetric, and setting `src` is itself what kicks off the media load.
    if (el.getAttribute('src') !== src) el.setAttribute('src', src);
    // `Promise.resolve(...)` because older engines (and jsdom) return undefined from play().
    try {
      Promise.resolve(el.play()).catch(conceal);
    } catch {
      conceal();
    }

    return () => {
      el.removeEventListener('playing', reveal);
      el.removeEventListener('timeupdate', reveal);
      el.removeEventListener('error', conceal);
      releaseBackgroundVideo(el);
    };
  }, [videoEnabled, src]);

  return { videoRef, videoEnabled, videoVisible };
}

export default useGateBackgroundVideo;
